import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Minus, Plus, AlertTriangle, Phone, MessageCircle, ArrowLeft, Check,
  ArrowUpRight, Download, FileText, Sliders, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { pimg, SITE, formatCategoryName } from "../lib/site";
import { useInquiry } from "../context/InquiryContext";
import { useSettings } from "../context/SettingsContext";
import { Reveal } from "../components/motion/Reveal";
import ProductCard from "../components/ProductCard";
import SectionHeading from "../components/SectionHeading";
import { formatWhatsAppUrl } from "../components/WhatsAppFloat";
import PageMeta from "../components/PageMeta";

const ProductDetail = () => {
  const { id } = useParams();
  const site = useSettings();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [activeTab, setActiveTab] = useState("description");
  const { addItem } = useInquiry();

  useEffect(() => {
    setImgIdx(0);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setQty(r.data.moq);
        return api.get(`/products?category=${encodeURIComponent(r.data.category)}`);
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1400px] px-5 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-medium">Product not found</h1>
        <Link to="/products" className="btn-dark mt-8">Back to Products</Link>
      </div>
    );

  if (!product)
    return (
      <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-16 lg:grid-cols-2 lg:px-10">
        <div className="aspect-square animate-pulse bg-brand-smoke" />
        <div className="h-96 animate-pulse bg-brand-smoke" />
      </div>
    );

  const belowMoq = qty < product.moq;

  return (
    <div data-testid="product-detail-page">
      <PageMeta
        title={product.name}
        description={product.tagline || product.description}
        image={product.images?.[0]}
      />
      <div className="mx-auto max-w-[1400px] px-5 py-10 md:py-16 lg:px-10">
        <Link to="/products" className="mono-caption inline-flex items-center gap-2 text-neutral-400 hover:text-brand-red" data-testid="back-to-products">
          <ArrowLeft size={14} /> Back to Products
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          {/* Gallery */}
          <Reveal>
            <div className="relative border border-black/10 bg-brand-smoke">
              <img
                src={pimg(product.images[imgIdx], 1000, 1000)}
                alt={product.name}
                className="aspect-square w-full object-cover"
                data-testid="product-main-image"
              />
              <span className="absolute left-4 top-4 bg-brand-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                {formatCategoryName(product.category)}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-square overflow-hidden border transition-colors ${
                    imgIdx === i ? "border-brand-red" : "border-black/10 hover:border-brand-ink"
                  }`}
                >
                  <img src={pimg(img, 300, 300)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1}>
            <h1 className="font-display text-4xl font-semibold tracking-tighter md:text-5xl" data-testid="product-name">
              {product.name}
            </h1>
            <p className="mt-3 text-lg text-neutral-500">{product.tagline}</p>

            <div className="mt-6 inline-flex items-center gap-3 border border-brand-red/40 bg-brand-red/5 px-4 py-2.5" data-testid="product-moq">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">MOQ</span>
              <span className="font-mono text-sm font-bold text-brand-red">
                {product.moq} {product.moq_unit || "pieces"}
              </span>
            </div>

            {product.price_tiers?.length > 0 && (
              <div className="mt-4 border border-black/10" data-testid="price-tiers">
                <div className="flex items-center justify-between border-b border-black/10 bg-brand-smoke px-4 py-2.5">
                  <span className="mono-caption text-neutral-500">Wholesale Pricing</span>
                  <span className="font-mono text-[11px] text-neutral-400">
                    Min. order: {product.moq} {product.moq_unit || "pieces"}
                  </span>
                </div>
                {product.price_tiers.map((t, i, arr) => {
                  const next = arr[i + 1];
                  const active = qty >= t.min_qty && (!next || qty < next.min_qty);
                  const range = next ? `${t.min_qty} – ${next.min_qty - 1}` : `${t.min_qty}+`;
                  return (
                    <div
                      key={t.min_qty}
                      data-testid={`price-tier-${t.min_qty}`}
                      className={`flex items-center justify-between px-4 py-3 transition-colors ${
                        active ? "bg-brand-red/5" : ""
                      } ${i > 0 ? "border-t border-black/5" : ""}`}
                    >
                      <span className={`font-mono text-sm ${active ? "font-bold text-brand-ink" : "text-neutral-500"}`}>
                        {range} {product.moq_unit || "pieces"}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className={`font-mono text-sm font-bold ${active ? "text-brand-red" : "text-brand-ink"}`}>
                          US ${Number(t.price).toFixed(2)}
                        </span>
                        {active && (
                          <span className="bg-brand-red px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white" data-testid="active-tier-badge">
                            Your tier
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
                {qty >= product.moq && (
                  <div className="flex items-center justify-between border-t border-brand-ink/20 px-4 py-3" data-testid="tier-estimated-total">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Est. total</span>
                    <span className="font-mono text-base font-bold text-brand-ink">
                      US ${(
                        qty *
                        (product.price_tiers.reduce(
                          (best, t) => (qty >= t.min_qty ? t.price : best),
                          product.price_tiers[0].price
                        ))
                      ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Customization */}
            <p className="label-pp mt-8">Customization Available</p>
            <div className="flex flex-wrap gap-2" data-testid="product-customization">
              {product.customization.map((c) => (
                <span key={c} className="inline-flex items-center gap-2 border border-black/15 px-3 py-1.5 text-xs">
                  <Check size={12} className="text-brand-red" /> {c}
                </span>
              ))}
            </div>

            {/* Options */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between">
                  <label className="label-pp" htmlFor="pd-color">Color</label>
                  <a
                    href="/PANTONE.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] text-brand-red underline hover:text-brand-reddark transition-colors inline-flex items-center gap-0.5"
                    data-testid="detail-pantone-color-link"
                  >
                    Pantone Guide ↗
                  </a>
                </div>
                <select id="pd-color" data-testid="product-color-select" value={color} onChange={(e) => setColor(e.target.value)} className="input-pp">
                  <option value="">Select color</option>
                  {product.colors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-pp" htmlFor="pd-size">Size</label>
                <select id="pd-size" data-testid="product-size-select" value={size} onChange={(e) => setSize(e.target.value)} className="input-pp">
                  <option value="">Select size</option>
                  {product.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Quantity + MOQ */}
            <div className="mt-8">
              <label className="label-pp">Required Quantity</label>
              <div className="flex items-stretch border border-brand-ink/30" data-testid="quantity-stepper">
                <button onClick={() => setQty(Math.max(1, qty - 10))} data-testid="qty-minus" className="flex w-12 items-center justify-center hover:bg-brand-smoke" aria-label="Decrease quantity">
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  value={qty}
                  min={1}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                  data-testid="qty-input"
                  className="h-12 w-full border-x border-brand-ink/30 text-center font-mono outline-none"
                />
                <button onClick={() => setQty(qty + 10)} data-testid="qty-plus" className="flex w-12 items-center justify-center hover:bg-brand-smoke" aria-label="Increase quantity">
                  <Plus size={16} />
                </button>
              </div>
              {belowMoq && (
                <div className="mt-3 flex items-start gap-3 border border-brand-red bg-brand-red/5 p-4" data-testid="moq-warning">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0 text-brand-red" />
                  <p className="font-mono text-xs leading-relaxed text-brand-red">
                    Minimum order quantity for this product is {product.moq} {product.moq_unit || "pieces"}.
                  </p>
                </div>
              )}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() =>
                  addItem({ product_id: product.id, product_name: product.name, quantity: qty, color, size })
                }
                data-testid="detail-add-to-inquiry"
                className="btn-dark flex-1"
              >
                Add to Inquiry
              </button>
              <Link to={`/inquiry?product=${product.id}`} className="btn-primary flex-1" data-testid="detail-request-quote">
                Request Quote
              </Link>
            </div>
            {(() => {
              const rawContact = site?.social_links?.whatsapp || site?.phone || "+923001123344";
              const customWhatsAppMsg = `Hi ${site?.name || "Pickleballgearz"}, I would like to inquire about wholesale / OEM production:\n- Product: ${product.name} (SKU: ${product.sku || product.model_number || (product.numeric_id ? `PKL-${product.numeric_id}` : "PKL-001")})\n- Quantity: ${qty} units\n${color ? `- Color: ${color}\n` : ""}${size ? `- Size: ${size}\n` : ""}- Link: ${window.location.href}`;
              const whatsappUrl = formatWhatsAppUrl(rawContact, customWhatsAppMsg);

              return (
                <div className="mt-3 flex flex-wrap gap-3">
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white px-4 py-3 text-xs font-semibold uppercase tracking-wider transition-colors duration-200"
                    data-testid="detail-whatsapp-inquire"
                  >
                    <MessageCircle size={16} /> Inquire via WhatsApp
                  </a>
                  <a
                    href={site.phoneLink}
                    className="inline-flex items-center justify-center gap-2 border border-black/15 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-700 hover:bg-black hover:text-white transition-colors duration-200"
                    data-testid="detail-call"
                  >
                    <Phone size={15} /> Call
                  </a>
                </div>
              );
            })()}
          </Reveal>
        </div>

        {/* WOOCOMMERCE STYLE PRODUCT TABS */}
        <div className="mt-16 border-t border-black/10 pt-10 md:mt-24 md:pt-14" data-testid="product-tabs-section">
          <div className="flex flex-wrap border-b border-black/10 gap-2 sm:gap-4">
            {[
              { id: "description", label: "Description", icon: FileText },
              { id: "specs", label: "Specifications", icon: Sliders },
              { id: "pantone", label: "Pantone", icon: Palette },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`product-tab-${tab.id}`}
                  className={`inline-flex items-center gap-2 border-b-2 px-5 py-3.5 font-mono text-xs uppercase tracking-[0.18em] font-semibold transition-all ${
                    isActive
                      ? "border-brand-red text-brand-ink bg-black/[0.02]"
                      : "border-transparent text-neutral-400 hover:border-black/20 hover:text-brand-ink"
                  }`}
                >
                  <Icon size={15} className={isActive ? "text-brand-red" : "text-neutral-400"} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="py-8 md:py-12">
            {/* 1. DESCRIPTION TAB */}
            {activeTab === "description" && (
              <div className="max-w-4xl space-y-6" data-testid="tab-panel-description">
                <div>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-brand-ink">
                    Product Overview
                  </h3>
                  <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-neutral-600">
                    {product.description || "Premium tournament-grade pickleball equipment engineered for competitive play, high spin dynamics, and long-term durability."}
                  </p>
                </div>

                {product.customization?.length > 0 && (
                  <div className="border-t border-black/10 pt-6">
                    <h4 className="font-display text-lg font-medium text-brand-ink">OEM / Customization Capabilities</h4>
                    <p className="mt-1 text-sm text-neutral-500">
                      Our factory supports extensive custom branding options for this model:
                    </p>
                    <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                      {product.customization.map((c) => (
                        <li key={c} className="flex items-center gap-2 text-sm text-neutral-700">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-red/10 text-brand-red">
                            <Check size={12} />
                          </span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* 2. SPECIFICATIONS TAB */}
            {activeTab === "specs" && (
              <div className="max-w-4xl" data-testid="tab-panel-specs">
                <h3 className="font-display text-2xl font-medium tracking-tight text-brand-ink mb-6">
                  Technical Specifications
                </h3>
                <div className="border border-black/10 divide-y divide-black/10 font-mono text-sm bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4 bg-brand-smoke/50">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Material / Construction</span>
                    <span className="sm:col-span-2 text-brand-ink">{product.material || "Standard Composite"}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Available Sizes</span>
                    <span className="sm:col-span-2 text-brand-ink">{product.sizes?.join(" / ") || "Standard"}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4 bg-brand-smoke/50">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Colorways</span>
                    <span className="sm:col-span-2 text-brand-ink flex flex-wrap items-center gap-2">
                      <span>{product.colors?.join(" / ") || "Custom"}</span>
                      <a
                        href="/PANTONE.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-xs text-brand-red underline hover:text-brand-reddark"
                      >
                        Pantone Matching Available <ArrowUpRight size={13} />
                      </a>
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Model / SKU</span>
                    <span className="sm:col-span-2 text-brand-ink">
                      {product.model_number && product.sku && product.model_number !== product.sku
                        ? `${product.model_number} / ${product.sku}`
                        : (product.sku || product.model_number || (product.numeric_id ? `PKL-${product.numeric_id}` : "PKL-001"))}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4 bg-brand-smoke/50">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Minimum Order Qty</span>
                    <span className="sm:col-span-2 text-brand-ink">{product.moq} {product.moq_unit || "pieces"}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Production Lead Time</span>
                    <span className="sm:col-span-2 text-brand-ink">15–25 business days (Sample: 7–10 days)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4 bg-brand-smoke/50">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Packaging</span>
                    <span className="sm:col-span-2 text-brand-ink">Custom retail box, protective bubble sleeve, or export bulk carton</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 p-4">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-semibold">Compliance</span>
                    <span className="sm:col-span-2 text-brand-ink">USAPA Standard Specs Compliant / Batch QC Guaranteed</span>
                  </div>
                </div>
              </div>
            )}

            {/* 3. PANTONE TAB */}
            {activeTab === "pantone" && (
              <div className="space-y-6" data-testid="tab-panel-pantone">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-black/10 bg-brand-smoke p-6">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-brand-ink px-2.5 py-1 text-white font-mono text-[10px] uppercase tracking-[0.2em] mb-2">
                      <Palette size={12} className="text-brand-red" />
                      Pantone Matching System (PMS)
                    </div>
                    <h3 className="font-display text-2xl font-medium tracking-tight text-brand-ink">
                      Official Factory Pantone Color Guide
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500 max-w-2xl">
                      We calibrate our UV printing, edge guard plastics, silicone grip bands, and packaging inks directly to official Pantone codes. Pick your exact shades below and mention them in your RFQ.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 shrink-0">
                    <a
                      href="/PANTONE.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-dark inline-flex items-center gap-2"
                      data-testid="pantone-tab-fullscreen"
                    >
                      Open Fullscreen PDF <ArrowUpRight size={16} />
                    </a>
                    <a
                      href="/PANTONE.pdf"
                      download="Pickleballgearz_PANTONE_Guide.pdf"
                      className="btn-outline inline-flex items-center gap-2 bg-white"
                      data-testid="pantone-tab-download"
                    >
                      Download PDF <Download size={16} />
                    </a>
                  </div>
                </div>

                <div className="border border-black/15 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between border-b border-black/10 bg-neutral-50 px-4 py-2 text-xs font-mono text-neutral-500">
                    <span>PANTONE.pdf — Interactive Document Viewer</span>
                    <a
                      href="/PANTONE.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-red underline hover:text-brand-reddark inline-flex items-center gap-1"
                    >
                      Open in New Tab <ArrowUpRight size={12} />
                    </a>
                  </div>
                  <iframe
                    src="/PANTONE.pdf#toolbar=1&navpanes=0"
                    title="Pantone Color Guide"
                    className="w-full h-[680px] border-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="border-t border-black/10 bg-brand-smoke py-20 md:py-28">
          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
            <SectionHeading number="REL" title="Related Products" />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
